import type { TrpcRouter } from '@strength/trpc-server';
import {
  SERVER_ROLE_PERMISSION_ERROR,
  CLIENT_ROUTE_PERMISSION_ERROR,
} from '@strength/domain';
import { httpLink, TRPCClientError } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { z } from 'zod';
import { environment } from '../environment';

export type RouterInput = inferRouterInputs<TrpcRouter>;
export type RouterOutput = inferRouterOutputs<TrpcRouter>;

export function isTRPCClientError(
  cause: unknown,
): cause is TRPCClientError<TrpcRouter> {
  return cause instanceof TRPCClientError;
}

/** Check if error is a client-side route permission error (triggers error boundary). */
export const isClientRoutePermissionError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.startsWith(CLIENT_ROUTE_PERMISSION_ERROR);

type RolePermissionErrorBody = {
  error?: {
    message?: string;
    data?: {
      cause?: {
        roleChanged?: boolean;
        deniedRoute?: string;
      };
    };
  };
};

/** Check if the tRPC error response contains a server role permission error. */
function isServerRolePermissionError(
  body: unknown,
): body is RolePermissionErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    (body as RolePermissionErrorBody).error?.message ===
      SERVER_ROLE_PERMISSION_ERROR
  );
}

/**
 * Builds the `tw-team-ids` header value from an array of team ID strings.
 * Returns an empty object when the array is empty.
 */
export function buildTeamIdsHeader(teamIds: string[]): Record<string, string> {
  if (teamIds.length === 0) return {};
  return { 'tw-team-ids': JSON.stringify(teamIds) };
}

/**
 * Module-level tablet team IDs written by the mounted tablet screen via
 * setTabletTeamIdsHeader. The selection screen pushes `[selectorTeamId]`; the
 * collection screen pushes the union of the selected athletes' team IDs. Both
 * clear the store (push `[]`) on unmount.
 */
let _tabletTeamIds: string[] = [];

/** Called by the tablet screen to keep the header in sync with the current scope. */
export function setTabletTeamIdsHeader(teamIds: string[]): void {
  _tabletTeamIds = teamIds;
}

/**
 * Returns the `tw-team-ids` header object to be sent with every tRPC request.
 * Pure function of (URL, pushed tablet array) — no localStorage reads.
 *
 * Priority:
 *  1. Web (non-tablet): first URL path segment if it is a valid UUID.
 *  2. Tablet: the array pushed by the mounted tablet screen via
 *     setTabletTeamIdsHeader (empty array → `{}`).
 */
export function getTeamIdsHeader(): Record<string, string> {
  // 1. URL-based (non-tablet pages expose the team ID as the first path segment)
  const firstSegment = window.location.pathname.split('/')[1] ?? '';
  if (z.uuid().safeParse(firstSegment).success) {
    return buildTeamIdsHeader([firstSegment]);
  }

  // 2. Tablet — pushed directly by the mounted tablet screen.
  return buildTeamIdsHeader(_tabletTeamIds);
}

export function getTrpcClientOptions(onAuthError: () => void) {
  return {
    links: [
      httpLink({
        url: `${environment.VITE_API_URL}/trpc`,
        headers: () => getTeamIdsHeader(),
        async fetch(url, options) {
          const result = await fetch(url, {
            ...options,
            credentials: 'include',
          });
          if (result.status === 401) {
            onAuthError();
          }
          if (result.status === 403) {
            // Check if this is a role-based permission error
            const resultCopy = result.clone();

            let body: unknown;
            try {
              body = await resultCopy.json();
            } catch {
              // JSON parse failed (not valid JSON) - skip role error handling
              return result;
            }

            // Now handle the parsed body outside the try/catch
            if (isServerRolePermissionError(body)) {
              const roleChanged = body.error?.data?.cause?.roleChanged;
              if (roleChanged) {
                window.location.href = '/';
              } else {
                throw new Error(CLIENT_ROUTE_PERMISSION_ERROR);
              }
            }
            return result;
          }
          return result;
        },
      }),
    ],
  };
}
